<template>
  <v-container
    fluid
    class="d-flex justify-center align-center pa-0 parent">
    <div
      aspect-ration="1.41"
      class="pa-0 certificate-wrapper"
      :class="{'fullscreen-margin': $vuetify.breakpoint.mdAndUp}"
      width="30"
      aria-live="polite">
      <v-row class="ma-0">
        <v-col
          class="col-8 ma-0 left-col">
          <div class="image-wrapper">
            <img
              :src="school.logo"
              alt="school logo"
              aria-label="school logo"
              class="logo"
              contain
              :style="{'background-color': school.activeColor}">
          </div>
          <h1 class="text-uppercase verified mb-2">Certificate of Completion</h1>
          <h2
            class="completed-date subheader"
          >
            {{completedDate}}
          </h2>
          <h1
            class="student-name"
            :style="{'color': nameColor}"
            :class="nameClass">
            {{studentName}}
          </h1>
          <p
            class="body-text mb-2"
          >
            has successfully completed all requirements for the playlist.
          </p>
          <h2
            class="mb-2 subheader"
          >
            {{playlistName}}
          </h2>
          <p
            class="body-text mb-2"
          >
            a {{modules && modules.length}} module playlist assigned by {{school.name}} {{hasAlternateBranding ? '' : 'and facilitated by PlayPosit'}}
          </p>
          <div v-if="messages.length" class="fine-print">
            <p v-for="(message, i) in messages" :key="i" class="mb-2">{{message}}</p>
          </div>
          <img
            class="signature"
            v-if="certificateSignature"
            :src="certificateSignature"
            alt="Certificate signature">
        </v-col>
        <v-col class="col-4 pa-0 d-flex flex-column align-center righ-col">
          <div
            class="ribbon-box d-flex justify-center body-text"
          >
            <div>
              <img
                class="badge mt-2 pa-4"
                :src="certificateBadge ? certificateBadge : 'https://fastio.playposit.com/attachments/1600292643383-bcf5c470-f865-11ea-99c9-4bdeebe04a23.svg'"
                alt="Certificate badge">
            </div>
          </div>
          <v-container
            class="d-flex flex-column align-center link-container"
          >
            <p
              class="url-header body-text">
              Verify at
            </p>
            <a
              :href="url"
              class="url text-center body-text"
              :style="linkStyle"
            >
              {{displayUrl}}
            </a>
          </v-container>
          <v-container
            v-if="hasAlternateBranding"
            class="d-flex flex-column align-center">
            <v-img
              src="https://bunny.playposit.com/images/PlayPositByWeVideo.png"
              contain
              height="45"
              class="altBranding">
            </v-img>
          </v-container>
        </v-col>
      </v-row>
    </div>
    <v-btn
      fixed
      fab
      bottom
      right
      color="primary"
      class="remove-when-printing"
      @click="print"
      aria-label="A button that opens the print dialog so that you can print this certificate page."
    >
      <v-icon
        color="white">
        print
      </v-icon>
    </v-btn>
  </v-container>
</template>

<script>
import {
  mapState,
  mapActions,
  mapMutations,
  mapGetters,
} from 'vuex';
import _ from 'lodash';
import moment from 'moment';

moment().format();

/* global dataLayer */
/* eslint consistent-return: 0 */
export default {
  name: 'PrintableCertificate',
  data: () => ({
    completedDate: '<date of playlist completion>',
    displayUrl: null,
    messages: [],
    modules: null,
    nameColor: '#2196F3',
    playlistName: null,
    school: {},
    studentName: null,
    url: null,
  }),
  computed: {
    ...mapState([
      'courseHash',
      'playlist',
      'playlistId',
      'sectionId',
      'userToken',
      'certificateBadge',
      'certificateMessage',
      'certificateSignature',
    ]),
    ...mapGetters([
      'queryParams',
    ]),
    hasAlternateBranding() {
      return _.get(this, 'school.config.permissions', []).includes('alternatePlaylistCertificateBranding');
    },
    linkStyle() {
      return {
        color: this.nameColor,
      };
    },
    nameClass() {
      if (this.studentName && this.studentName.length > 30) {
        return 'extra-small-name';
      }
      if (this.studentName && this.studentName.length > 20) {
        return 'small-name';
      }
      return 'name';
    },
  },
  methods: {
    ...mapActions([
      'getSimplifiedPlaylist',
      'decodeUserId',
    ]),
    ...mapMutations([
      'setCertificateBadge',
      'setCertificateMessage',
      'setCertificateSignature',
      'setSectionId',
      'setPlaylistId',
      'setQueryParams',
      'setUserToken',
    ]),
    blueScreenRedirect(props) {
      this.$enablePlaypositErrorScreen({
        vertical: true,
        active_color: '#2196F3',
        base_color: '#0d47a1',
        inactive_color: '#757575',
        title: _.has(props, 'title') ? props.title : 'Error',
        subtitle: _.has(props, 'subtitle') ? props.subtitle : 'Sorry, something has gone wrong.',
        code: _.has(props, 'code') ? props.code : '',
        text: _.has(props, 'text') ? props.text : '',
        icon: _.has(props, 'icon') ? props.icon : 'error',
        buttons: _.has(props, 'buttons') ? props.buttons : [],
      });
      dataLayer.errorLog(props);
    },
    findCompletionDate() {
      const completionTimes = [];
      for (const module of this.modules) {
        for (const moduleBulb of module.moduleBulbs) {
          completionTimes.push(moduleBulb?.bulbAttempt?.completionTime || '2022-07-22');
        }
      }
      const completedDate = new Date(Math.max.apply(null, completionTimes.map(time => new Date(time))));
      this.completedDate = moment(completedDate).format('LL');
    },
    print() {
      window.print();
    },
  },
  async created() {
    if (this.$route.name === '404') {
      return this.blueScreenRedirect({
        title: '404',
        subtitle: 'That page can\'t be found',
        text: 'Check the URL you entered or try launching your course from your user dash.',
        status: 404,
        location: `/course/${window.location.pathname}`,
      });
    }
    try {
      this.setQueryParams(new URLSearchParams(window.location.search));
      this.setPlaylistId(this.$route.params.courseHash);
      const { data: { playlistData, schoolData, studentData } } = await this.getSimplifiedPlaylist({
        playlistId: this.playlistId,
        encodedId: this.$route.params.certificateHash,
        sectionId: this.sectionId,
      });
      this.nameColor = playlistData.defaultColor;
      this.setCertificateBadge(playlistData.certificateBadge);
      this.setCertificateSignature(playlistData.certificateSignature);
      this.setCertificateMessage(playlistData.certificateMessage);
      const messages = this.certificateMessage ? this.certificateMessage.split('\n') : [];
      this.messages = messages.filter(message => message.trim().length > 0);
      const [, currentUrl] = window.location.href.split('//');
      this.url = studentData ? window.location.href : null;
      this.displayUrl = studentData ? currentUrl : '<url will go here>';
      this.studentName = studentData ? `${studentData.firstName} ${studentData.lastName}` : 'Test Student';
      this.playlistName = playlistData.name;
      this.modules = playlistData.modules;
      if (studentData && !schoolData) {
        return this.blueScreenRedirect({
          title: 'Unable to Retrieve Certificate',
          subtitle: 'No sectionId passed',
          text: 'Please check your section id or try relaunching from your course page.',
          status: 404,
          location: `/course/${window.location.pathname}`,
        });
      }
      if (studentData && schoolData) {
        this.findCompletionDate();
      }
      if (schoolData) {
        // refactor to use teacherPlaylist to find the teacher.school
        this.school = _.get(schoolData, 'teacher.school') ? _.get(schoolData, 'teacher.school') : {};
        this.$injectCssFile(_.get(this, 'school.cssInjection', ''));
        if (!this.school.logo) {
          this.school.logo = 'https://cdn.playposit.com/image-uploads/image-22-11-2019-20-54-37--logo_155.png';
        }
        if (!this.school.activeColor) {
          this.school.activeColor = '#2196F3';
        }
      } else {
        this.school = {
          name: 'Demo School',
          logo: 'https://cdn.playposit.com/image-uploads/image-22-11-2019-20-54-37--logo_155.png',
          activeColor: '#2196F3',
        };
      }
    } catch(e) {
      this.blueScreenRedirect({
        title: 'Unable to Retrieve Certificate',
        subtitle: e.message,
        text: 'Trouble retrieving certificate.',
      });
    }
  },
};
</script>

<style lang="scss" scoped>
  @import url('https://fonts.googleapis.com/css2?family=Martel&display=swap');
  html,
  .parent {
    height: 100%;
    width: 100%;
    background-color: gray;
  }
  .fullscreen-margin {
    margin: 30px;
  }
  h1, h2, p, a {
    color: #555555;
    font-family: "Martel", serif;
  }
  a {
    color: var(--color);
  }
  .body-text {
    font-size: 14px;
  }
  .verified {
    border-bottom: 10px solid #555555;
    letter-spacing: 2px;
    margin-top: 40px;
  }
  .completed-date, .verified {
    font-size: 1.2em;
  }
  .completed-date {
    margin-bottom: 20px;
  }
  .link-container {
    border-top: 7px solid #555555;
    border-bottom: 7px solid #555555;
    width: 12em !important;
    margin-top: 20px;
  }
  .certificate-wrapper {
    position: relative;
    background: white;
    height: 42em;
    width: 55em;
    background-image: url("https://fastio.playposit.com/attachments/1600292626841-b319a890-f865-11ea-a957-7b50aa001409.svg"), url("https://fastio.playposit.com/attachments/1600292626841-b319a890-f865-11ea-a957-7b50aa001409.svg");
    background-position: 2% 98%, 98% 2%;
    background-repeat: no-repeat, no-repeat;
    background-size: 45%, 45%;
  }
  .left-col {
    padding-left: 32px;
    padding-right: 20px;
    padding-top: 20px;
  }
  .fine-print {
    font-size: 0.5em;
  }
  .image-wrapper {
    margin: 20px 0 0 20px;
    width: 100%;
    margin: 20px 0 0 20px;
  }
  .logo {
    max-height: 9em;
    min-height: 5em;
    padding: 1em;
    max-width: 100%;
  }
  .signature {
    height: 4em;
    position: absolute;
    bottom: 20px;
    left: 20px;
  }
  .altBranding {
    position: absolute;
    bottom: 30px;
  }
  .right-col {
    width: 100%;
  }
  .ribbon-box {
    background-image: url("https://bunny.playposit.com/images/ribbon75.svg");
    background-position: center center;
    background-repeat: no-repeat;
    background-size: cover;
    height: 28em;
    width: 14em;
  }
  .badge {
    display: block;
    height: auto;
    width: 100%;
  }
  .url {
    width: 16em;
    overflow-wrap: break-word;
    word-wrap: break-word;
    font-size: 14px;
  }
  .student-name {
    width: 100%;
    color: #2196F3;
    font-weight: 600;
    letter-spacing: 2px;
    overflow-wrap: break-word;
  }
  .name {
    font-size: 2.1em;
  }
  .small-name {
    font-size: 1.4em;
  }
  .extra-small-name {
    font-size: 1.2em;
  }
  .url-header {
    margin-bottom: 2px !important;
  }
  @media only screen and (max-width : 1024px) {
    .name {
      font-size: 1.5em;
    }
    .subheader, .verified, .completed-date {
      font-size: 18px;
    }
    .fine-print {
      font-size: 0.3em;
    }
    .body-text {
      font-size: 12px;
    }
    .small-name {
      font-size: 1.3em;
    }
    .logo {
      max-height: 6em;
    }
    .extra-small-name {
      font-size: 0.9em;
    }
    .certificate-wrapper {
      height: 31.5em;
      width: 41.25em;
    }
    .ribbon-box {
      height: 21em;
      width: 10.5em;
    }
    .verified {
      margin-top: 20px;
    }
    .signature {
      height: 2.5em;
    }
    .url {
      font-size: 8px;
    }
    .url-header {
      font-size: 12px;
    }
  }
  @media only screen and (max-width : 630px) {
    p {
      margin-bottom: 5px !important;
    }
    .body-text {
      font-size: 0.3em;
      padding-bottom: 0px !important;
    }
    .certificate-wrapper {
      height: 13.86em;
      width: 18.15em;
    }
    .fine-print {
      font-size: 0.2em;
    }
    .subheader, .verified, .completed-date {
      font-size: 0.3em;
    }
    .verified {
      border-bottom: 2px solid #555555;
      margin-top: 10px;
    }
    .body-text {
      font-size: 4.62px;
    }
    .completed-date {
      margin-bottom: 7px;
    }
    .image-wrapper {
      margin: 10px 0 0 10px;
    }
    .logo {
      height: 2em;
      max-height: 2.97em;
      min-height: 1.65em;
      padding: 0;
      max-width: 100%;
    }
    .left-col {
      padding: 0px 5px;
    }
    .link-container {
      border-top: 3px solid #555555;
      border-bottom: 3px solid #555555;
      width: 4em !important;
      padding: 3px;
      margin-top: 25px;
    }
    .name {
      font-size: 0.5em;
    }
    .small-name {
      font-size: 0.3em;
    }
    .subheader {
      font-size: 0.4em;
    }
    .extra-small-name {
      font-size: 0.3em;
    }
    .signature {
      height: 1.1em;
      bottom: 5px;
      left: 5px;
    }
    .ribbon-box {
      height: 28em;
      width: 14em;
    }
    .url {
      width: 7em;
      font-size: 6px;
    }
    .url-header {
      font-size: 8px;
    }
  }
  @media print {
    * {
      size: landscape;
    }
    .parent {
      display: inline;
      background-color: white !important;
      padding: 0 !important;
    }
    .certificate-wrapper {
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      margin: 0 !important;
    }
    .remove-when-printing {
      display: none !important;
    }
    .certificate-wrapper {
      padding: 0;
    }
    .logo {
      height: 9em;
    }
    .name {
      font-size: 2.1em;
    }
    .small-name {
      font-size: 1.4em;
    }
    .extra-small-name {
      font-size: 1.2em;
    }
    .subheader, .verified, .completed-date {
      font-size: 24px;
    }
    .body-text {
      font-size: 16px;
    }
    .fine-print {
      font-size: 0.45em;
    }
    .signature {
      height: 3.5em;
      bottom: 0px;
      left: 0px;
    }
    .link-container {
      width: 12em !important;
    }
    .url {
      width: 16em
    }
    .altBranding {
      position: absolute;
      bottom: 0px;
    }
  }
</style>
